import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'chat-trigger',
  version: '2.0.0',
  properties: [
    // ============================================
    // PARAMETERS TAB - WORKFLOW BEHAVIOR
    // ============================================
    {
      key: 'initialMessages',
      label: 'Initial Message(s)',
      type: 'string',
      typeOptions: {
        rows: 4,
      },
      default: 'Hi there! 👋\nMy name is Evie. How can I assist you today?',
      placeholder: 'Enter initial greeting messages...',
      description: 'Messages shown when the chat first loads (one per line)',
      section: 'properties',
    },
    {
      key: 'inputPlaceholder',
      label: 'Input Placeholder',
      type: 'string',
      default: 'Type your question...',
      placeholder: 'Type your message...',
      description: 'Placeholder text in the message input field',
      section: 'properties',
    },
    {
      key: 'allowFileUploads',
      label: 'Allow File Uploads',
      type: 'boolean',
      default: false,
      description: 'Allow users to upload files in the chat',
      section: 'properties',
    },
    {
      key: 'allowedFileMimeTypes',
      label: 'Allowed File Mime Types',
      type: 'string',
      default: 'image/*,application/pdf,.doc,.docx,.txt',
      placeholder: 'image/*,application/pdf',
      description: 'Comma-separated list of allowed file types (e.g. image/*, application/pdf)',
      section: 'properties',
      displayOptions: {
        show: {
          allowFileUploads: [true],
        },
      },
    },

    // ============================================
    // DEPLOYMENT TAB - ACCESS & SECURITY
    // ============================================
    {
      key: 'mode',
      label: 'Mode',
      type: 'options',
      required: true,
      default: 'mfe',
      options: [
        {
          value: 'mfe',
          name: 'Micro Frontend (MFE)',
          description: 'Use in Evo Spaces, modals, or external embeds (Recommended)',
        },
        {
          value: 'embeddedChat',
          name: 'Embedded Chat',
          description: 'Generate iframe code to embed on external websites',
        },
      ],
      description: 'Choose how users will interact with your chat',
      section: 'deployment',
    },

    // ============================================
    // MFE MODE DEPLOYMENT SETTINGS
    // ============================================
    {
      key: 'mfeIntegrationButton',
      label: 'MFE Integration',
      type: 'button',
      default: 'View Integration Guide',
      description: 'Click to see how to integrate this chat as an MFE',
      section: 'deployment',
      displayOptions: {
        show: {
          mode: ['mfe'],
        },
      },
    },
    {
      key: 'allowedChatUsers',
      label: 'Visible to',
      type: 'chatVisibility',
      default: [],
      required: false,
      description: 'Select users who can see this chat in the MFE. Leave empty to keep it completely private (no one can see it, not even you).',
      section: 'deployment',
      displayOptions: {
        show: {
          mode: ['mfe'],
        },
      },
    },

    // ============================================
    // EMBEDDED MODE DEPLOYMENT SETTINGS
    // ============================================
    {
      key: 'embedCode',
      label: 'Embed Code',
      type: 'notice',
      default: '',
      description: 'Copy this code to embed the chat widget on your website',
      section: 'deployment',
      displayOptions: {
        show: {
          mode: ['embeddedChat'],
        },
      },
    },
    {
      key: 'allowedOrigins',
      label: 'Allowed Origins (CORS)',
      type: 'string',
      default: '*',
      placeholder: 'https://example.com, https://app.example.com',
      description: 'Comma-separated list of allowed CORS origins (* = all)',
      section: 'deployment',
      displayOptions: {
        show: {
          mode: ['embeddedChat'],
        },
      },
    },
    {
      key: 'testInput',
      label: 'Test Input',
      type: 'json',
      required: false,
      placeholder: '{\n  "message": "Hello, this is a test message"\n}',
      description: 'Test data to use when testing this workflow (simulates a chat message). Should include message, sessionId, and optionally userId.',
      default: '{\n  "message": "Hello, this is a test message"\n}',
      section: 'properties',
    },
  ],
};

export default schema;
